import React, { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../services/firebase";
import { getSeekerProfile } from "../services/seekerProfileService";
import {
  unlockContact,
  checkDatabaseAccess,
  getUnlockedContacts,
} from "../services/databaseAccessService";
import LoadingSpinner from "../components/LoadingSpinner";

// Filter Sidebar Component (outside main component to prevent re-renders)
const FilterSidebar = ({ filters, updateFilter, onClearFilters }) => (
  <div className="space-y-4">
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        Filters
      </h2>
      <div className="space-y-3">
        {/* Search Keywords */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Search Keywords
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Name, role, skills..."
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <svg
              className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Experience Level */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Experience
          </label>
          <select
            value={filters.experience}
            onChange={(e) => updateFilter("experience", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value="any">Any Experience</option>
            <option value="0-1">0-1 Years</option>
            <option value="1-3">1-3 Years</option>
            <option value="3-5">3-5 Years</option>
            <option value="5-10">5-10 Years</option>
            <option value="10+">10+ Years</option>
          </select>
        </div>

        {/* Location */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Location
          </label>
          <input
            type="text"
            placeholder="City, State..."
            value={filters.location}
            onChange={(e) => updateFilter("location", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Salary Range */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Salary Range (Annual)
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              placeholder="Min (₹)"
              value={filters.minSalary}
              onChange={(e) => updateFilter("minSalary", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <input
              type="number"
              placeholder="Max (₹)"
              value={filters.maxSalary}
              onChange={(e) => updateFilter("maxSalary", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Skills */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Skills
          </label>
          <input
            type="text"
            placeholder="e.g. React, Python"
            value={filters.skills}
            onChange={(e) => updateFilter("skills", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Education */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Education
          </label>
          <input
            type="text"
            placeholder="e.g. B.Tech, MBA"
            value={filters.education}
            onChange={(e) => updateFilter("education", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Notice Period */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">
            Notice Period
          </label>
          <select
            value={filters.noticePeriod}
            onChange={(e) => updateFilter("noticePeriod", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value="any">Any</option>
            <option value="immediate">Immediate</option>
            <option value="15">15 Days or less</option>
            <option value="30">30 Days or less</option>
            <option value="60">60 Days or less</option>
            <option value="90+">90+ Days</option>
          </select>
        </div>
      </div>

      {/* Clear Filters */}
      <button
        onClick={onClearFilters}
        className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
      >
        Clear All Filters
      </button>
    </div>
  </div>
);

const SeekerDatabase = ({ userData }) => {
  const [user] = useAuthState(auth);
  const [seekers, setSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const seekersPerPage = 20;

  // Filter State - Only fields shown in seeker profile
  const [filters, setFilters] = useState({
    search: "",
    experience: "any",
    location: "",
    minSalary: "",
    maxSalary: "",
    education: "",
    skills: "",
    noticePeriod: "any",
  });

  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [unlockedContacts, setUnlockedContacts] = useState(new Set());
  const [unlockError, setUnlockError] = useState(null);
  const [databaseAccess, setDatabaseAccess] = useState(null);
  const isSuperAdmin = userData?.userType === 'superadmin';
  const [unlocking, setUnlocking] = useState(false);

  // Detailed View Modal State
  const [selectedSeeker, setSelectedSeeker] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [selectedResumeUrl, setSelectedResumeUrl] = useState(null);

  // Load previously unlocked contacts and check database access
  useEffect(() => {
    const loadUnlockedContacts = async () => {
      if (!user) return;

      // Super admin has access to all contacts without package purchase
      if (isSuperAdmin) {
        return;
      }

      try {
        // Check database access
        const accessResult = await checkDatabaseAccess(user.uid);
        if (accessResult.success) {
          setDatabaseAccess(accessResult);
        }

        // Load unlocked contacts
        const unlockedResult = await getUnlockedContacts(user.uid);
        if (unlockedResult.success) {
          const unlockedIds = new Set(
            unlockedResult.contacts.map((c) => c.seekerId)
          );
          setUnlockedContacts(unlockedIds);
        }
      } catch (error) {
        console.error("Error loading unlocked contacts:", error);
      }
    };

    loadUnlockedContacts();
  }, [user, isSuperAdmin]);

  useEffect(() => {
    const fetchSeekers = async () => {
      try {
        const q = query(
          collection(db, "users"),
          where("userType", "==", "jobseeker")
        );
        const snapshot = await getDocs(q);
        const seekersData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const userData = { id: doc.id, ...doc.data() };

            // Fetch seeker profile
            try {
              const result = await getSeekerProfile(userData.id);
              if (result.success) {
                userData.seekerProfile = result.profile;
              }
            } catch (err) {
              console.error(
                `Error fetching seeker profile for ${userData.id}:`,
                err
              );
            }

            return userData;
          })
        );

        // Filter out users without email/phone AND users without meaningful profile data
        const validSeekers = seekersData.filter(seeker => {
          const hasEmail = seeker.email || seeker.seekerProfile?.personalInfo?.email;
          const hasPhone = seeker.phone || seeker.seekerProfile?.personalInfo?.phone;

          // Check if user has meaningful profile data beyond just contact info
          const profile = seeker.seekerProfile || {};
          const hasExperience = profile.experience && Array.isArray(profile.experience) && profile.experience.length > 0;
          const hasEducation = profile.education && Array.isArray(profile.education) && profile.education.length > 0;
          const hasSkills = profile.skills && Array.isArray(profile.skills) && profile.skills.length > 0;
          const hasLocation = profile.personalInfo?.location;
          const hasPreferences = profile.preferences && (
            profile.preferences.targetRoles ||
            profile.preferences.expectedSalary ||
            profile.preferences.preferredLocations
          );

          // User must have contact info AND at least one meaningful profile field
          const hasMeaningfulData = hasExperience || hasEducation || hasSkills || hasLocation || hasPreferences;

          return hasEmail && hasPhone && hasMeaningfulData;
        });

        setSeekers(validSeekers);
      } catch (error) {
        console.error("Error fetching seekers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSeekers();
  }, []);

  // Filter Logic
  const filteredSeekers = seekers.filter((seeker) => {
    const profile = seeker.seekerProfile || {};
    const personal = profile.personalInfo || {};
    const exp = profile.experience || [];
    const edu = profile.education || [];
    const prefs = profile.preferences || {};
    const skills = profile.skills || [];

    // Search (Global) - Name, Skills, Role, Location, Company
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const nameMatch = `${seeker.firstName} ${seeker.lastName}`
        .toLowerCase()
        .includes(searchLower);
      const skillMatch = skills.some((s) =>
        s.toLowerCase().includes(searchLower)
      );
      const roleMatch = exp.some((e) =>
        e.jobTitle?.toLowerCase().includes(searchLower)
      );
      const locMatch = personal.location?.toLowerCase().includes(searchLower);
      const companyMatch = exp.some((e) =>
        e.company?.toLowerCase().includes(searchLower)
      );

      if (!nameMatch && !skillMatch && !roleMatch && !locMatch && !companyMatch)
        return false;
    }

    // Experience Level
    if (filters.experience !== "any") {
      const totalYears = exp.reduce(
        (acc, curr) => acc + (parseInt(curr.yearsOfExperience) || 0),
        0
      );
      if (filters.experience === "0-1" && totalYears > 1) return false;
      if (filters.experience === "1-3" && (totalYears < 1 || totalYears > 3))
        return false;
      if (filters.experience === "3-5" && (totalYears < 3 || totalYears > 5))
        return false;
      if (filters.experience === "5-10" && (totalYears < 5 || totalYears > 10))
        return false;
      if (filters.experience === "10+" && totalYears < 10) return false;
    }

    // Expected Salary Range
    if (filters.minSalary) {
      const candidateSalary =
        prefs.expectedSalary?.min || prefs.expectedSalary?.max || 0;
      if (parseInt(candidateSalary) < parseInt(filters.minSalary)) return false;
    }
    if (filters.maxSalary) {
      const candidateSalary =
        prefs.expectedSalary?.max || prefs.expectedSalary?.min || 999999999;
      if (parseInt(candidateSalary) > parseInt(filters.maxSalary)) return false;
    }

    // Preferred Work Locations
    if (filters.location) {
      const locationLower = filters.location.toLowerCase();
      const currentLocMatch = personal.location
        ?.toLowerCase()
        .includes(locationLower);
      const preferredLocMatch =
        prefs.preferredLocations &&
        Array.isArray(prefs.preferredLocations) &&
        prefs.preferredLocations.some((loc) =>
          loc.toLowerCase().includes(locationLower)
        );

      if (!currentLocMatch && !preferredLocMatch) return false;
    }

    // Education Qualification
    if (filters.education) {
      const eduLower = filters.education.toLowerCase();
      const eduMatch = edu.some(
        (e) =>
          e.degree?.toLowerCase().includes(eduLower) ||
          e.fieldOfStudy?.toLowerCase().includes(eduLower) ||
          e.institution?.toLowerCase().includes(eduLower)
      );
      if (!eduMatch) return false;
    }

    // Skills
    if (filters.skills) {
      const skillQuery = filters.skills.toLowerCase();
      if (!skills.some((s) => s.toLowerCase().includes(skillQuery)))
        return false;
    }

    // Notice Period (Simplified)
    if (filters.noticePeriod !== "any") {
      const noticePeriod =
        prefs.noticePeriod || personal.noticePeriod || "not-specified";
      if (
        filters.noticePeriod === "immediate" &&
        noticePeriod !== "immediate" &&
        noticePeriod !== "0" &&
        noticePeriod !== "15"
      )
        return false;
      if (
        filters.noticePeriod === "15" &&
        !["15", "30", "immediate", "0"].includes(noticePeriod)
      )
        return false;
      if (
        filters.noticePeriod === "30" &&
        !["30", "15", "immediate", "0"].includes(noticePeriod)
      )
        return false;
      if (
        filters.noticePeriod === "60" &&
        !["60", "30", "15", "immediate", "0"].includes(noticePeriod)
      )
        return false;
      if (filters.noticePeriod === "90+" && noticePeriod === "90+")
        return false;
    }

    return true;
  });

  // Reset to first page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [JSON.stringify(filters)]);

  const handleUnlock = async (seekerId) => {
    if (unlocking) return;

    // Super admin doesn't need to unlock - they have access to all
    if (isSuperAdmin) {
      return;
    }

    try {
      setUnlocking(true);
      setUnlockError(null);

      if (!user) {
        alert("Please login to unlock contact details");
        return;
      }

      // Find the seeker data
      const seeker = seekers.find((s) => s.id === seekerId);
      if (!seeker) {
        alert("Seeker not found");
        return;
      }

      // Get contact details from seeker profile
      const profile = seeker.seekerProfile || {};
      const personal = profile.personalInfo || {};

      const seekerData = {
        id: seeker.id,
        firstName: seeker.firstName,
        lastName: seeker.lastName,
        email: personal.email || seeker.email,
        phone: personal.phone || seeker.phone,
        seekerProfile: profile,
      };

      // Attempt to unlock contact
      const result = await unlockContact(user.uid, seekerData);

      if (!result.success) {
        if (result.alreadyUnlocked) {
          // Already unlocked, just add to state
          setUnlockedContacts((prev) => new Set(prev).add(seekerId));
        } else {
          alert(
            result.error ||
            "Failed to unlock contact. Please purchase a database package first."
          );
        }
        return;
      }

      // Success - add to unlocked contacts and update access info
      setUnlockedContacts((prev) => new Set(prev).add(seekerId));

      // Update database access info
      const accessResult = await checkDatabaseAccess(user.uid);
      if (accessResult.success) {
        setDatabaseAccess(accessResult);
      }

      alert(
        `Contact unlocked successfully! Remaining contacts: ${result.remainingContacts}`
      );
    } catch (error) {
      console.error("Error unlocking contact:", error);
      alert("Failed to unlock contact. Please try again.");
    } finally {
      setUnlocking(false);
    }
  };
  // Text Highlighting Helper - Only highlight when search is active
  const HighlightText = ({ text, highlight }) => {
    if (!text) return null;
    if (!highlight || highlight.trim() === "") return <span>{text}</span>;

    try {
      const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const parts = text.toString().split(new RegExp(`(${escaped})`, "gi"));
      return (
        <span>
          {parts.map((part, i) =>
            part.toLowerCase() === highlight.toLowerCase() ? (
              <span
                key={i}
                className="bg-yellow-200 text-gray-900 font-medium rounded px-0.5"
              >
                {part}
              </span>
            ) : (
              part
            )
          )}
        </span>
      );
    } catch (e) {
      return <span>{text}</span>;
    }
  };

  // Pagination
  const indexOfLastSeeker = currentPage * seekersPerPage;
  const indexOfFirstSeeker = indexOfLastSeeker - seekersPerPage;
  const currentSeekers = filteredSeekers.slice(
    indexOfFirstSeeker,
    indexOfLastSeeker
  );
  const totalPages = Math.ceil(filteredSeekers.length / seekersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    const listTop = document.getElementById("seeker-list-top");
    if (listTop) listTop.scrollIntoView({ behavior: "smooth" });
  };

  // Update filter handler
  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Clear filters handler
  const handleClearFilters = useCallback(() => {
    setFilters({
      search: "",
      experience: "any",
      minSalary: "",
      maxSalary: "",
      location: "",
      education: "",
      skills: "",
      noticePeriod: "any",
    });
  }, []);

  const exportSeekers = () => {
    if (filteredSeekers.length === 0) return;

    const headers = [
      'Name',
      'Email',
      'Phone',
      'Current Role',
      'Experience',
      'Skills',
      'Location',
      'Education',
      'Notice Period',
      'Expected Salary (Min)',
      'Expected Salary (Max)',
      'Resume Link'
    ];

    const csvContent = [
      headers,
      ...filteredSeekers.map(seeker => {
        const profile = seeker.seekerProfile || {};
        const personal = profile.personalInfo || {};
        const exp = profile.experience || [];
        const edu = profile.education || [];
        const prefs = profile.preferences || {};

        // Calculate total experience - use yearsOfExperience if available, else calculate from dates
        const totalExperience = exp.reduce((acc, curr) => {
          if (curr.yearsOfExperience) return acc + (parseInt(curr.yearsOfExperience) || 0);
          if (curr.startDate) {
            const start = new Date(curr.startDate);
            const end = curr.isCurrentJob || !curr.endDate ? new Date() : new Date(curr.endDate);
            const years = (end - start) / (1000 * 60 * 60 * 24 * 365.25);
            return acc + Math.max(0, parseFloat(years.toFixed(1)));
          }
          return acc;
        }, 0);

        // Get education string
        const educationStr = edu.map(e => {
          const parts = [e.degree, e.fieldOfStudy && `in ${e.fieldOfStudy}`, e.institution].filter(Boolean);
          return parts.join(' ');
        }).join('; ');

        // Get skills - handle both array and string
        const skillsStr = Array.isArray(profile.skills)
          ? profile.skills.join(', ')
          : (profile.skills || 'N/A');

        // Get salary - handle both string and number values
        const salaryMin = prefs.expectedSalary?.min
          ? `Rs.${parseInt(prefs.expectedSalary.min).toLocaleString('en-IN')}`
          : 'N/A';
        const salaryMax = prefs.expectedSalary?.max
          ? `Rs.${parseInt(prefs.expectedSalary.max).toLocaleString('en-IN')}`
          : 'N/A';

        // Get notice period
        const noticePeriod = prefs.noticePeriod || personal.noticePeriod || 'N/A';

        // Get resume URL
        const resumeLink = profile.resumeURL || 'N/A';

        return [
          `${seeker.firstName || ''} ${seeker.lastName || ''}`.trim(),
          personal.email || seeker.email || 'N/A',
          personal.phone || seeker.phone || 'N/A',
          exp[0]?.jobTitle || 'Not Specified',
          totalExperience > 0 ? `${parseFloat(totalExperience.toFixed(1))} yrs` : 'Fresher',
          skillsStr,
          personal.location || 'N/A',
          educationStr || 'N/A',
          noticePeriod,
          salaryMin,
          salaryMax,
          resumeLink
        ].map(field => {
          const stringField = (field ?? '').toString().replace(/"/g, '""');
          return `"${stringField}"`;
        });
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seeker_database_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Handle seeker card click to show detailed view
  const handleSeekerClick = (seeker) => {
    setSelectedSeeker(seeker);
    setShowDetailModal(true);
  };

  // Handle resume view
  const handleViewResume = (resumeUrl, event) => {
    if (event) event.stopPropagation();

    if (!resumeUrl) {
      alert('No resume available for this user.');
      return;
    }

    setSelectedResumeUrl(resumeUrl);
    setResumeModalOpen(true);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-gray-50">
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-72 bg-white border-r border-gray-200 h-full overflow-y-auto p-6 flex-shrink-0">
        <FilterSidebar
          filters={filters}
          updateFilter={updateFilter}
          onClearFilters={handleClearFilters}
        />
      </div>

      {/* Mobile Filter Toggle */}
      <div className="md:hidden fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setShowMobileFilters(true)}
          className="bg-indigo-600 text-white p-4 rounded-full shadow-xl hover:bg-indigo-700 transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Sidebar Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-gray-900 bg-opacity-50"
            onClick={() => setShowMobileFilters(false)}
          ></div>
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white p-6 overflow-y-auto shadow-2xl animate-slideLeft">
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowMobileFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <FilterSidebar
              filters={filters}
              updateFilter={updateFilter}
              onClearFilters={handleClearFilters}
            />
          </div>
        </div>
      )}

      {/* Main Content Area - Scrollable */}
      <div
        className="flex-1 h-full overflow-y-auto p-4 md:p-8"
        id="seeker-list-top"
      >
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                Job Seeker Database
              </h1>
              <p className="text-gray-600 mt-1">
                Showing {filteredSeekers.length} qualified professionals
              </p>
            </div>

            {isSuperAdmin && (
              <button
                onClick={exportSeekers}
                disabled={filteredSeekers.length === 0}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${filteredSeekers.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
                  }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export Database
              </button>
            )}
          </div>

          {/* Database Access Status Banner - Hidden for Super Admin */}
          {user && !isSuperAdmin && databaseAccess && (
            <div
              className={`mb-6 rounded-lg p-4 ${databaseAccess.hasAccess
                ? "bg-green-50 border border-green-200"
                : "bg-orange-50 border border-orange-200"
                }`}
            >
              {databaseAccess.hasAccess ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        Active Package: {databaseAccess.packageName}
                      </p>
                      <p className="text-xs text-green-700">
                        {databaseAccess.remainingContacts} of{" "}
                        {databaseAccess.totalContacts} contacts remaining
                        {databaseAccess.expiryDate &&
                          ` • Expires: ${databaseAccess.expiryDate.toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <a
                    href="/packages"
                    className="text-sm font-medium text-green-700 hover:text-green-900 underline"
                  >
                    Upgrade
                  </a>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-orange-800">
                        No Active Package
                      </p>
                      <p className="text-xs text-orange-700">
                        Purchase a database package to unlock contact details
                      </p>
                    </div>
                  </div>
                  <a
                    href="/packages"
                    className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700"
                  >
                    Buy Package
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Error Alert - Also hidden for Super Admin generally, unless error occurs during manual unlock attempted (unlikely for SA) */}
          {unlockError && !isSuperAdmin && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800 mb-1">
                  Unable to Unlock Contact
                </h3>
                <p className="text-sm text-red-700">{unlockError}</p>
                {unlockError.includes("purchase a package") && (
                  <a
                    href="/packages"
                    className="inline-block mt-2 text-sm font-medium text-red-600 hover:text-red-800 underline"
                  >
                    View Available Packages →
                  </a>
                )}
              </div>
              <button
                onClick={() => setUnlockError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )
          }

          <div className="space-y-6">
            {currentSeekers.map((seeker) => {
              const profile = seeker.seekerProfile || {};
              const experience = profile.experience || [];
              const currentJob =
                experience.find((e) => e.isCurrentJob) || experience[0] || {};
              const education = profile.education?.[0] || {};
              const personal = profile.personalInfo || {};
              const preferences = profile.preferences || {};
              const skills = profile.skills || [];
              // Super admin has access to all contacts without unlocking
              const isUnlocked = isSuperAdmin || unlockedContacts.has(seeker.id);

              // Get email and phone with fallback to base user data
              const email = personal.email || seeker.email;
              const phone = personal.phone || seeker.phone;

              return (
                <div
                  key={seeker.id}
                  onClick={() => handleSeekerClick(seeker)}
                  className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex gap-3 sm:gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-lg sm:text-xl">
                        {seeker.firstName?.charAt(0)?.toUpperCase()}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Header with Name and Unlock Button */}
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {filters.search ? (
                              <HighlightText
                                text={`${seeker.firstName} ${seeker.lastName}`}
                                highlight={filters.search}
                              />
                            ) : (
                              `${seeker.firstName} ${seeker.lastName}`
                            )}
                          </h3>

                          {/* Experience, Salary, Location Row */}
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              <svg
                                className="w-4 h-4 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                              </svg>
                              <span>
                                {experience.length
                                  ? `${experience.reduce(
                                    (acc, curr) =>
                                      acc +
                                      (parseInt(curr.yearsOfExperience) || 0),
                                    0
                                  )} yrs ${experience.reduce(
                                    (acc, curr) =>
                                      acc + (parseInt(curr.months) || 0),
                                    0
                                  )} mos`
                                  : "Fresher"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg
                                className="w-4 h-4 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span>
                                {preferences.expectedSalary?.min
                                  ? `${(
                                    preferences.expectedSalary.min / 100000
                                  ).toFixed(1)} Lakh`
                                  : "Not Disclosed"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg
                                className="w-4 h-4 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              {filters.search || filters.location ? (
                                <HighlightText
                                  text={personal.location || "Not specified"}
                                  highlight={filters.location || filters.search}
                                />
                              ) : (
                                personal.location || "Not specified"
                              )}
                            </div>
                          </div>

                          {/* Job Type, Work Mode, Notice Period Row */}
                          {(preferences.preferredJobTypes ||
                            preferences.preferredWorkModes ||
                            preferences.noticePeriod ||
                            personal.noticePeriod) && (
                              <div className="flex items-center gap-3 text-xs text-gray-500 mb-2 flex-wrap">
                                {preferences.preferredJobTypes &&
                                  Array.isArray(preferences.preferredJobTypes) &&
                                  preferences.preferredJobTypes.length > 0 && (
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                                      {preferences.preferredJobTypes.join(", ")}
                                    </span>
                                  )}
                                {preferences.preferredWorkModes &&
                                  Array.isArray(preferences.preferredWorkModes) &&
                                  preferences.preferredWorkModes.length > 0 && (
                                    <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded">
                                      {preferences.preferredWorkModes.join(", ")}
                                    </span>
                                  )}
                                {(preferences.noticePeriod ||
                                  personal.noticePeriod) && (
                                    <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded">
                                      Notice:{" "}
                                      {preferences.noticePeriod ||
                                        personal.noticePeriod}
                                    </span>
                                  )}
                              </div>
                            )}
                        </div>

                        {/* Unlock Button */}
                        <div className="sm:ml-4 flex-shrink-0">
                          {!isUnlocked ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnlock(seeker.id);
                              }}
                              className="px-4 py-2 bg-orange-500 text-white rounded-md font-medium text-sm hover:bg-orange-600 transition-colors flex items-center gap-2 whitespace-nowrap"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                />
                              </svg>
                              Unlock
                            </button>
                          ) : (
                            <div className="text-sm space-y-1">
                              <a
                                href={`tel:${phone}`}
                                className="text-green-600 font-medium hover:underline block"
                              >
                                📞 {phone}
                              </a>
                              <a
                                href={`mailto:${email}`}
                                className="text-blue-600 font-medium hover:underline block break-all"
                              >
                                ✉️ {email}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Applied For / Preferred Job Roles */}
                      {preferences.targetRoles && preferences.targetRoles.length > 0 && (
                        <div className="mb-3">
                          <div className="flex items-start gap-2">
                            <svg
                              className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            <div className="flex-1">
                              <span className="text-sm text-gray-600 font-medium">
                                Applied For / पसंतीच्या नोकरी भूमिका
                              </span>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {preferences.targetRoles.slice(0, 3).map((role, i) => (
                                  <span
                                    key={i}
                                    className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded border border-purple-200 font-medium"
                                  >
                                    {role}
                                  </span>
                                ))}
                                {preferences.targetRoles.length > 3 && (
                                  <span className="text-xs text-purple-600 font-medium">
                                    +{preferences.targetRoles.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Current/Latest Job */}
                      <div className="mb-3">
                        <div className="flex items-start gap-2">
                          <svg
                            className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                          <div>
                            <span className="text-sm text-gray-600 font-medium">
                              Current / Latest
                            </span>
                            <p className="text-sm text-gray-900">
                              {filters.search ? (
                                <>
                                  <span className="bg-yellow-100 text-gray-900 px-1">
                                    <HighlightText
                                      text={
                                        currentJob.jobTitle ||
                                        "Role Not Specified"
                                      }
                                      highlight={filters.search}
                                    />
                                  </span>
                                  {currentJob.company && (
                                    <span className="ml-1">
                                      at{" "}
                                      <HighlightText
                                        text={currentJob.company}
                                        highlight={filters.search}
                                      />
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  {currentJob.jobTitle || "Role Not Specified"}
                                  {currentJob.company && (
                                    <span className="ml-1">
                                      at {currentJob.company}
                                    </span>
                                  )}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Education */}
                      <div className="mb-3">
                        <div className="flex items-start gap-2">
                          <svg
                            className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          </svg>
                          <div>
                            <span className="text-sm text-gray-600 font-medium">
                              Education
                            </span>
                            <p className="text-sm text-gray-900">
                              {education.degree ? (
                                <>
                                  {filters.education || filters.search ? (
                                    <>
                                      <HighlightText
                                        text={education.degree}
                                        highlight={
                                          filters.education || filters.search
                                        }
                                      />
                                      {education.fieldOfStudy && (
                                        <span>
                                          ,{" "}
                                          <span className="bg-yellow-100 text-gray-900 px-1">
                                            <HighlightText
                                              text={education.fieldOfStudy}
                                              highlight={
                                                filters.education ||
                                                filters.search
                                              }
                                            />
                                          </span>
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {education.degree}
                                      {education.fieldOfStudy && (
                                        <span>, {education.fieldOfStudy}</span>
                                      )}
                                    </>
                                  )}
                                  {education.institution && (
                                    <span>, {education.institution}</span>
                                  )}
                                </>
                              ) : (
                                "Not specified"
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Preferred Locations & Industries */}
                      {((preferences.preferredLocations &&
                        Array.isArray(preferences.preferredLocations) &&
                        preferences.preferredLocations.length > 0) ||
                        (preferences.preferredIndustries &&
                          Array.isArray(preferences.preferredIndustries) &&
                          preferences.preferredIndustries.length > 0) ||
                        (preferences.preferredJobRoles &&
                          Array.isArray(preferences.preferredJobRoles) &&
                          preferences.preferredJobRoles.length > 0)) && (
                          <div className="mb-3 space-y-2">
                            {preferences.preferredLocations &&
                              Array.isArray(preferences.preferredLocations) &&
                              preferences.preferredLocations.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <svg
                                    className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                  </svg>
                                  <div>
                                    <span className="text-sm text-gray-600 font-medium">
                                      Pref. Location
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {Array.isArray(preferences.preferredLocations)
                                        ? preferences.preferredLocations.join(", ")
                                        : preferences.preferredLocations}
                                    </p>
                                  </div>
                                </div>
                              )}
                            {preferences.preferredIndustries &&
                              Array.isArray(preferences.preferredIndustries) &&
                              preferences.preferredIndustries.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <svg
                                    className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                    />
                                  </svg>
                                  <div>
                                    <span className="text-sm text-gray-600 font-medium">
                                      Pref. Industry
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {preferences.preferredIndustries.join(", ")}
                                    </p>
                                  </div>
                                </div>
                              )}
                            {preferences.preferredJobRoles &&
                              Array.isArray(preferences.preferredJobRoles) &&
                              preferences.preferredJobRoles.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <svg
                                    className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <div>
                                    <span className="text-sm text-gray-600 font-medium">
                                      Pref. Roles
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {preferences.preferredJobRoles.join(", ")}
                                    </p>
                                  </div>
                                </div>
                              )}
                          </div>
                        )}

                      {/* Skills */}
                      {skills.length > 0 && (
                        <div className="mb-3">
                          <div className="flex items-start gap-2">
                            <svg
                              className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                              />
                            </svg>
                            <div className="flex-1">
                              <span className="text-sm text-gray-600 font-medium">
                                Skills
                              </span>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {skills.slice(0, 6).map((skill, i) => (
                                  <span
                                    key={i}
                                    className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded border border-gray-200"
                                  >
                                    {filters.skills || filters.search ? (
                                      <HighlightText
                                        text={skill}
                                        highlight={
                                          filters.skills || filters.search
                                        }
                                      />
                                    ) : (
                                      skill
                                    )}
                                  </span>
                                ))}
                                {skills.length > 6 && (
                                  <span className="text-xs text-blue-600 cursor-pointer hover:underline">
                                    +{skills.length - 6} more
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Languages */}
                      {profile.languages &&
                        Array.isArray(profile.languages) &&
                        profile.languages.length > 0 && (
                          <div>
                            <div className="flex items-start gap-2">
                              <svg
                                className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                                />
                              </svg>
                              <div>
                                <span className="text-sm text-gray-600 font-medium">
                                  Languages
                                </span>
                                <p className="text-sm text-gray-900">
                                  {profile.languages.map((lang, i) => (
                                    <span key={i}>
                                      {lang.language}
                                      {lang.proficiency && (
                                        <span className="text-gray-500">
                                          ({" "}
                                          <span className="text-blue-600">
                                            {lang.proficiency}
                                          </span>{" "}
                                          )
                                        </span>
                                      )}
                                      {i < profile.languages.length - 1 &&
                                        " | "}
                                    </span>
                                  ))}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Resume Button on Card */}
                      {profile.resumeURL && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewResume(profile.resumeURL, e);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-lg text-xs font-medium hover:bg-cyan-100 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                            </svg>
                            View Resume
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {
            totalPages > 1 && filteredSeekers.length > 0 && (
              <div className="mt-8 flex justify-center pb-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-1 flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded-md font-medium text-sm transition-colors ${currentPage === 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    Prev
                  </button>

                  {/* Page Numbers - visible on desktop/tablet, hidden on mobile */}
                  <div className="hidden sm:flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) =>
                      i + 1 === 1 ||
                        i + 1 === totalPages ||
                        (i + 1 >= currentPage - 1 && i + 1 <= currentPage + 1) ? (
                        <button
                          key={i}
                          onClick={() => handlePageChange(i + 1)}
                          className={`w-8 h-8 rounded-md font-medium text-sm transition-colors ${currentPage === i + 1
                            ? "bg-indigo-600 text-white"
                            : "text-gray-600 hover:bg-gray-50"
                            }`}
                        >
                          {i + 1}
                        </button>
                      ) : i + 1 === currentPage - 2 || i + 1 === currentPage + 2 ? (
                        <span key={i} className="px-1 text-gray-400">
                          ...
                        </span>
                      ) : null
                    )}
                  </div>

                  {/* Mobile page indicator */}
                  <span className="sm:hidden text-xs font-medium text-gray-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 rounded-md font-medium text-sm transition-colors ${currentPage === totalPages
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )
          }

          {
            filteredSeekers.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-dashed border-gray-300 mt-6">
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  No professionals found
                </h3>
                <p className="text-sm text-gray-500">
                  Try adjusting your filters.
                </p>
                <button
                  onClick={() =>
                    setFilters({
                      search: "",
                      experience: "any",
                      minSalary: "",
                      maxSalary: "",
                      location: "",
                      education: "",
                      skills: "",
                    })
                  }
                  className="mt-4 px-4 py-2 bg-indigo-50 text-indigo-700 text-sm rounded-lg font-medium hover:bg-indigo-100 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )
          }
        </div >
      </div >

      {/* Detailed Seeker View Modal */}
      {
        showDetailModal && selectedSeeker && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowDetailModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8 flex flex-col max-h-[90vh] md:max-h-[95vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 p-6 pr-12 md:pr-16 border-b border-gray-200 rounded-t-2xl">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white font-bold text-xl sm:text-2xl flex-shrink-0">
                    {selectedSeeker.firstName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white break-words pr-4">
                      {selectedSeeker.firstName} {selectedSeeker.lastName}
                    </h2>
                    <p className="text-xs sm:text-sm text-white text-opacity-90">
                      Complete Profile Details
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="absolute top-4 right-4 z-10 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-1"
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="hidden sm:inline text-xs">Close</span>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
                {(() => {
                  const profile = selectedSeeker.seekerProfile || {};
                  const personal = profile.personalInfo || {};
                  const experience = profile.experience || [];
                  const education = profile.education || [];
                  const preferences = profile.preferences || {};
                  const skills = profile.skills || [];
                  const languages = profile.languages || [];
                  const isUnlocked = isSuperAdmin || unlockedContacts.has(selectedSeeker.id);
                  const email = personal.email || selectedSeeker.email;
                  const phone = personal.phone || selectedSeeker.phone;

                  return (
                    <div className="space-y-6">
                      {/* Contact Information Section */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                          Contact Information
                        </h3>
                        {isUnlocked ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                              <p className="text-xs font-medium text-gray-500 mb-1">Email Address</p>
                              <a href={`mailto:${email}`} className="text-sm text-blue-600 font-medium hover:underline flex items-center gap-2">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                {email}
                              </a>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                              <p className="text-xs font-medium text-gray-500 mb-1">Phone Number</p>
                              <a href={`tel:${phone}`} className="text-sm text-green-600 font-medium hover:underline flex items-center gap-2">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                {phone}
                              </a>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <svg className="w-6 h-6 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <div>
                                  <p className="text-sm font-semibold text-orange-800">Contact Details Locked</p>
                                  <p className="text-xs text-orange-700">Click "Unlock Contact" to view email and phone number</p>
                                </div>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlock(selectedSeeker.id);
                                }}
                                className="w-full sm:w-auto px-4 py-2 bg-orange-500 text-white rounded-md font-medium text-sm hover:bg-orange-600 transition-colors whitespace-nowrap text-center"
                              >
                                Unlock Contact
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Personal Information */}
                      {personal.location && (
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            Personal Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Current Location</p>
                              <p className="text-sm text-gray-900 font-medium">{personal.location}</p>
                            </div>
                            {/* {personal.linkedIn && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">LinkedIn Profile</p>
                              <a href={personal.linkedIn} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                                View LinkedIn Profile →
                              </a>
                            </div>
                          )} */}
                          </div>
                        </div>
                      )}

                      {/* Experience Section */}
                      {experience.length > 0 && (
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                              <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                            </svg>
                            Work Experience
                          </h3>
                          <div className="space-y-4">
                            {experience.map((exp, idx) => (
                              <div key={idx} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <h4 className="text-base font-bold text-gray-900">{exp.jobTitle || 'Position'}</h4>
                                    <p className="text-sm text-gray-700 font-medium">{exp.company || 'Company'}</p>
                                  </div>
                                  {exp.isCurrentJob && (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">Current</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                                  {exp.yearsOfExperience && (
                                    <span className="flex items-center gap-1">
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      {exp.yearsOfExperience} years {exp.months && `${exp.months} months`}
                                    </span>
                                  )}
                                  <span>{exp.startDate || 'Start'} - {exp.isCurrentJob ? 'Present' : (exp.endDate || 'End')}</span>
                                </div>
                                {exp.responsibilities && (
                                  <p className="text-sm text-gray-700 mt-2">{exp.responsibilities}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Education Section */}
                      {education.length > 0 && (
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                            </svg>
                            Education
                          </h3>
                          <div className="space-y-4">
                            {education.map((edu, idx) => (
                              <div key={idx} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-100">
                                <h4 className="text-base font-bold text-gray-900">{edu.degree || 'Degree'}</h4>
                                <p className="text-sm text-gray-700 font-medium">{edu.fieldOfStudy || 'Field of Study'}</p>
                                <p className="text-sm text-gray-600 mt-1">{edu.institution || 'Institution'}</p>
                                {edu.year && <p className="text-xs text-gray-500 mt-1">Year: {edu.year}</p>}
                                {edu.grade && <p className="text-xs text-gray-500">Grade: {edu.grade}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Skills Section */}
                      {skills.length > 0 && (
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                            </svg>
                            Skills
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {skills.map((skill, idx) => (
                              <span key={idx} className="px-3 py-1.5 bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-700 rounded-lg text-sm font-medium border border-orange-200">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Languages Section */}
                      {languages.length > 0 && (
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.087 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-2zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.788-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z" clipRule="evenodd" />
                            </svg>
                            Languages
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {languages.map((lang, idx) => (
                              <div key={idx} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-100">
                                <p className="text-sm font-bold text-gray-900">{lang.language}</p>
                                {lang.proficiency && (
                                  <p className="text-xs text-green-600 font-medium">{lang.proficiency}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Preferences Section */}
                      {(preferences.targetRoles || preferences.expectedSalary || preferences.preferredLocations || preferences.noticePeriod) && (
                        <div className="bg-white rounded-xl p-6 border border-gray-200">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                            </svg>
                            Job Preferences
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {preferences.targetRoles && (Array.isArray(preferences.targetRoles) ? preferences.targetRoles.length > 0 : preferences.targetRoles) && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-2">Target Roles</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {(Array.isArray(preferences.targetRoles) ? preferences.targetRoles : [preferences.targetRoles]).map((role, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded border border-purple-200">
                                      {role}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {preferences.expectedSalary && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Expected Salary</p>
                                <p className="text-sm text-gray-900 font-medium">
                                  ₹{(preferences.expectedSalary.min / 100000).toFixed(1)}L - ₹{(preferences.expectedSalary.max / 100000).toFixed(1)}L {preferences.expectedSalary.currency}
                                </p>
                              </div>
                            )}
                            {preferences.preferredLocations && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Preferred Locations</p>
                                <p className="text-sm text-gray-900">
                                  {Array.isArray(preferences.preferredLocations)
                                    ? preferences.preferredLocations.join(', ')
                                    : preferences.preferredLocations}
                                </p>
                              </div>
                            )}
                            {preferences.noticePeriod && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Notice Period</p>
                                <p className="text-sm text-gray-900 font-medium">{preferences.noticePeriod}</p>
                              </div>
                            )}
                            {preferences.preferredJobTypes && (Array.isArray(preferences.preferredJobTypes) ? preferences.preferredJobTypes.length > 0 : preferences.preferredJobTypes) && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Job Types</p>
                                <p className="text-sm text-gray-900">
                                  {Array.isArray(preferences.preferredJobTypes)
                                    ? preferences.preferredJobTypes.join(', ')
                                    : preferences.preferredJobTypes}
                                </p>
                              </div>
                            )}
                            {preferences.preferredWorkModes && (Array.isArray(preferences.preferredWorkModes) ? preferences.preferredWorkModes.length > 0 : preferences.preferredWorkModes) && (
                              <div>
                                <p className="text-xs font-medium text-gray-500 mb-1">Work Modes</p>
                                <p className="text-sm text-gray-900">
                                  {Array.isArray(preferences.preferredWorkModes)
                                    ? preferences.preferredWorkModes.join(', ')
                                    : preferences.preferredWorkModes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Resume Section */}
                      {profile.resumeURL && (
                        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
                          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                            </svg>
                            Resume
                          </h3>
                          <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-lg p-4 gap-4 shadow-sm">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate">{selectedSeeker.firstName}_{selectedSeeker.lastName}_Resume.pdf</p>
                                <p className="text-xs text-gray-500">Click to view or download resume</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                onClick={(e) => handleViewResume(profile.resumeURL, e)}
                                className="flex-1 md:flex-none px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View Resume
                              </button>
                              <a
                                href={profile.resumeURL}
                                download={`${selectedSeeker.firstName}_${selectedSeeker.lastName}_Resume.pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex-1 md:flex-none px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm text-center"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )
      }

      {/* Resume Viewer Modal */}
      {
        resumeModalOpen && selectedResumeUrl && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={() => setResumeModalOpen(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Resume Viewer</h2>
                    <p className="text-sm text-white text-opacity-90">{selectedSeeker?.firstName} {selectedSeeker?.lastName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Download Button */}
                  <a
                    href={selectedResumeUrl}
                    download={`${selectedSeeker?.firstName}_${selectedSeeker?.lastName}_Resume.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                    title="Download Resume"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </a>
                  {/* Print Button */}
                  <button
                    onClick={() => {
                      const printWindow = window.open(selectedResumeUrl, '_blank');
                      if (printWindow) {
                        printWindow.onload = () => {
                          printWindow.print();
                        };
                      }
                    }}
                    className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                    title="Print Resume"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print
                  </button>
                  {/* Close Button */}
                  <button
                    onClick={() => setResumeModalOpen(false)}
                    className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Close
                  </button>
                </div>
              </div>

              {/* Modal Body - PDF Viewer */}
              <div className="flex-1 overflow-hidden bg-gray-100 rounded-b-2xl">
                <iframe
                  src={`${selectedResumeUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  className="w-full h-full border-0"
                  title="Resume Viewer"
                  style={{ minHeight: '600px' }}
                />
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default SeekerDatabase;
